import React, { useState, useEffect } from 'react';
import { lookupMealById } from '../utils/api';
import { isFavorite, toggleFavorite } from '../utils/favorites';
import { Heart, X, Loader2, ChefHat, Globe, PlayCircle } from 'lucide-react';

function RecipeModal({ recipe, onClose, onFavoriteChange }) {
    const [recipeDetails, setRecipeDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchRecipeDetails = async () => {
            if (!recipe?.idMeal) return;

            setLoading(true);
            setError(null);

            try {
                const details = await lookupMealById(recipe.idMeal);
                if (details) {
                    setRecipeDetails(details);
                } else {
                    setError('Recipe details not found');
                }
            } catch (err) {
                setError('Failed to load recipe details');
            } finally {
                setLoading(false);
            }
        };

        fetchRecipeDetails();
    }, [recipe]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [onClose]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleFavoriteClick = () => {
        const newFavorites = toggleFavorite(recipeDetails || recipe);
        if (onFavoriteChange) {
            onFavoriteChange(newFavorites);
        }
    };

    const getIngredients = (meal) => {
        const ingredients = [];
        for (let i = 1; i <= 20; i++) {
            const ingredient = meal[`strIngredient${i}`];
            const measure = meal[`strMeasure${i}`];

            if (ingredient && ingredient.trim()) {
                ingredients.push({
                    ingredient: ingredient.trim(),
                    measure: measure ? measure.trim() : ''
                });
            }
        }
        return ingredients;
    };

    if (!recipe) return null;

    const currentRecipe = recipeDetails || recipe;
    const isLiked = isFavorite(currentRecipe.idMeal);

    return (
        <div className="modal-backdrop" onClick={handleBackdropClick}>
            <div className="recipe-modal">
                <div className="modal-header">
                    <h2>{currentRecipe.strMeal}</h2>
                    <div className="modal-actions">
                        <button
                            className={`favorite-btn ${isLiked ? 'liked' : ''}`}
                            onClick={handleFavoriteClick}
                            aria-label={isLiked ? 'Remove from favorites' : 'Add to favorites'}
                        >
                            <Heart size={20} fill={isLiked ? '#8b5cf6' : 'none'} />
                        </button>
                        <button
                            className="close-btn"
                            onClick={onClose}
                            aria-label="Close modal"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className="modal-content">
                    {loading ? (
                        <div className="loading">
                            <Loader2 className="loading-icon" />
                            Loading recipe details...
                        </div>
                    ) : error ? (
                        <div className="error">{error}</div>
                    ) : (
                        <>
                            <div className="recipe-image">
                                <img
                                    src={currentRecipe.strMealThumb}
                                    alt={currentRecipe.strMeal}
                                />
                            </div>

                            <div className="recipe-info">
                                {currentRecipe.strCategory && (
                                    <p><ChefHat size={16} /><strong>Category:</strong> {currentRecipe.strCategory}</p>
                                )}
                                {currentRecipe.strArea && (
                                    <p><Globe size={16} /><strong>Cuisine:</strong> {currentRecipe.strArea}</p>
                                )}
                            </div>

                            {recipeDetails && (
                                <>
                                    <div className="ingredients-section">
                                        <h3>Ingredients</h3>
                                        <ul className="ingredients-list">
                                            {getIngredients(recipeDetails).map((item, index) => (
                                                <li key={index}>
                                                    <span className="measure">{item.measure}</span>
                                                    <span className="ingredient">{item.ingredient}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="instructions-section">
                                        <h3>Instructions</h3>
                                        <div className="instructions">
                                            {recipeDetails.strInstructions?.split('\n').map((step, index) => (
                                                step.trim() && <p key={index}>{step.trim()}</p>
                                            ))}
                                        </div>
                                    </div>

                                    {recipeDetails.strYoutube && (
                                        <div className="video-section">
                                            <h3><PlayCircle size={20} />Video Tutorial</h3>
                                            <div className="video-container">
                                                <iframe
                                                    src={recipeDetails.strYoutube.replace('watch?v=', 'embed/')}
                                                    title={`${recipeDetails.strMeal} tutorial`}
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default RecipeModal;